// Create Campaign Drawer (WP-02 / WhatChimp §1.5).
// Full campaign authoring drawer matching WhatChimp Broadcast Center:
// name, multi-number account picker, approved template picker, live phone preview,
// dynamic variable overrides, header media uploader, targeting segment (labels, lists,
// country, custom fields, status), assign label after, delay seconds, send later/timezone,
// live debounced reach & cost preview, draft vs submit actions.

import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../lib/i18n';
import { useAccounts, ALL_ACCOUNTS, numberLabel } from '../../lib/accounts';
import { useTemplates, type TemplateView } from '../../lib/whatsappApi';
import { useLabels, useLists, useCustomFields } from '../../lib/crm';
import {
  whatsappCreateBroadcast,
  whatsappSubmitBroadcast,
  whatsappEstimateReach,
  type BroadcastSegment,
  type HeaderMedia,
} from '../../lib/broadcasts';
import Drawer from '../ui/Drawer';
import Button from '../ui/Button';
import { useToast } from '../ui/Toast';
import TemplatePreview from '../templates/TemplatePreview';
import MediaUploader from '../templates/MediaUploader';
import type { WaTemplateHeaderMedia } from '../../lib/templateTypes';
import {
  calculateSmsSegments,
  integrationProfileList,
  type IntegrationProfileDoc,
} from '../../lib/integrations';
import {
  ClockIcon,
  UserIcon,
  SmartphoneIcon,
  SparklesIcon,
} from '../icons';

interface CreateCampaignDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialChannel?: 'whatsapp' | 'sms';
}

const COMMON_TIMEZONES = [
  'Asia/Muscat',
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Kuwait',
  'Asia/Qatar',
  'Asia/Bahrain',
  'Africa/Cairo',
  'Europe/London',
  'Europe/Paris',
  'Asia/Kolkata',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];

export function CreateCampaignDrawer({
  open,
  onClose,
  onSuccess,
  initialChannel = 'whatsapp',
}: CreateCampaignDrawerProps) {
  const { t } = useI18n();
  const toast = useToast();
  const accounts = useAccounts();
  const { items: templates, loading: loadingTemplates } = useTemplates();
  const { labels } = useLabels();
  const { lists } = useLists();
  const { customFields } = useCustomFields();

  // Channel Selection
  const [channel, setChannel] = useState<'whatsapp' | 'sms'>(initialChannel);
  useEffect(() => {
    if (open && initialChannel) {
      setChannel(initialChannel);
    }
  }, [open, initialChannel]);

  // SMS Gateway Profiles & Message
  const [smsProfiles, setSmsProfiles] = useState<IntegrationProfileDoc[]>([]);
  const [selectedSmsProfileId, setSelectedSmsProfileId] = useState<string>('');
  const [smsBody, setSmsBody] = useState<string>('');
  const [loadingSmsProfiles, setLoadingSmsProfiles] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingSmsProfiles(true);
    integrationProfileList({ kind: 'sms' })
      .then((res) => {
        if (res.data.ok && res.data.profiles) {
          setSmsProfiles(res.data.profiles);
          if (!selectedSmsProfileId && res.data.profiles.length > 0) {
            setSelectedSmsProfileId(res.data.profiles[0].id);
          }
        }
      })
      .catch((err) => {
        toast.error((err as Error).message || t('bcast.sms.loadProfilesFailed') || 'Failed to load SMS profiles');
      })
      .finally(() => setLoadingSmsProfiles(false));
  }, [open]);

  const smsCalc = useMemo(() => calculateSmsSegments(smsBody), [smsBody]);

  // Basic Details
  const [name, setName] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');

  // Auto-select active account or first available
  useEffect(() => {
    if (!selectedAccountId && accounts.numbers.length > 0) {
      const active = accounts.selectedId !== ALL_ACCOUNTS ? accounts.selectedId : accounts.numbers[0].id;
      setSelectedAccountId(active);
    }
  }, [accounts.numbers, accounts.selectedId, selectedAccountId]);

  const selectedNumber = accounts.numbers.find((n) => n.id === selectedAccountId);

  // Template Selection
  const approvedTemplates = useMemo(() => {
    return templates.filter((tpl) => {
      if (tpl.status !== 'APPROVED') return false;
      if (selectedNumber?.businessAccountId && (tpl as any).wabaId) {
        return (tpl as any).wabaId === selectedNumber.businessAccountId;
      }
      return true;
    });
  }, [templates, selectedNumber?.businessAccountId]);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  // Reset template if no longer in approved list for selected account
  useEffect(() => {
    if (selectedTemplateId && !approvedTemplates.some((t) => t.id === selectedTemplateId)) {
      setSelectedTemplateId(approvedTemplates[0]?.id || '');
    } else if (!selectedTemplateId && approvedTemplates.length > 0) {
      setSelectedTemplateId(approvedTemplates[0].id);
    }
  }, [approvedTemplates, selectedTemplateId]);

  const activeTemplate: TemplateView | null = useMemo(() => {
    return approvedTemplates.find((t) => t.id === selectedTemplateId) || null;
  }, [approvedTemplates, selectedTemplateId]);

  // Extract variables needed by template
  const templateVariables = useMemo(() => {
    if (!activeTemplate) return [];
    const text = `${activeTemplate.body || ''} ${(activeTemplate as any).headerText || ''}`;
    const matches = Array.from(text.matchAll(/\{\{(\d+)\}\}/g));
    const indices = Array.from(new Set(matches.map((m) => Number(m[1])))).sort((a, b) => a - b);
    return indices;
  }, [activeTemplate]);

  // Variable Overrides
  const [variableOverrides, setVariableOverrides] = useState<Record<string, string>>({});

  // Reset overrides when template changes
  useEffect(() => {
    setVariableOverrides({});
  }, [selectedTemplateId]);

  // Header Media
  const headerComponent = activeTemplate?.components?.find((c) => c.type === 'HEADER');
  const hasMediaHeader =
    headerComponent?.format === 'IMAGE' ||
    headerComponent?.format === 'VIDEO' ||
    headerComponent?.format === 'DOCUMENT';
  const mediaHeaderFormat = (headerComponent?.format as 'IMAGE' | 'VIDEO' | 'DOCUMENT') || 'IMAGE';

  const [headerMedia, setHeaderMedia] = useState<WaTemplateHeaderMedia | null>(null);
  // Media uploaded when the template was created — sent automatically unless
  // the user uploads a replacement here (WhatsApp has no separate "cover").
  const templateMediaPath: string | null = (activeTemplate as any)?.headerMedia?.storagePath ?? null;
  const [localMediaPreview, setLocalMediaPreview] = useState<string | null>(null);

  // Segment Targeting
  const [includeLabelIds, setIncludeLabelIds] = useState<string[]>([]);
  const [excludeLabelIds, setExcludeLabelIds] = useState<string[]>([]);
  const [includeListIds, setIncludeListIds] = useState<string[]>([]);
  const [excludeListIds, setExcludeListIds] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'subscribed' | 'all'>('subscribed');

  // Custom Field Filter
  const [customFieldKey, setCustomFieldKey] = useState('');
  const [customFieldOp, setCustomFieldOp] = useState<'eq' | 'neq' | 'contains' | 'exists'>('eq');
  const [customFieldValue, setCustomFieldValue] = useState('');

  // Post-Send & Timing Options
  const [assignLabelAfter, setAssignLabelAfter] = useState<string>('');
  const [delaySeconds, setDelaySeconds] = useState<number>(0);
  const [scheduleType, setScheduleType] = useState<'now' | 'later'>('now');
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [timezone, setTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Muscat';
    } catch {
      return 'Asia/Muscat';
    }
  });

  // Reach & Pricing Estimation
  const [estimating, setEstimating] = useState(false);
  const [estimatedReach, setEstimatedReach] = useState<number | null>(null);
  const [estimatedCost, setEstimatedCost] = useState<number | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [submitting, setSubmitting] = useState(false);

  // Construct segment object
  const currentSegment: BroadcastSegment = useMemo(() => {
    const seg: BroadcastSegment = {
      status: statusFilter === 'all' ? undefined : 'subscribed',
      includeLabelIds: includeLabelIds.length > 0 ? includeLabelIds : undefined,
      excludeLabelIds: excludeLabelIds.length > 0 ? excludeLabelIds : undefined,
      includeListIds: includeListIds.length > 0 ? includeListIds : undefined,
      excludeListIds: excludeListIds.length > 0 ? excludeListIds : undefined,
      country: countryFilter.trim() ? countryFilter.trim().toUpperCase() : undefined,
      marketingOptInRequired: activeTemplate?.category === 'MARKETING',
    };

    if (customFieldKey.trim()) {
      seg.customField = {
        key: customFieldKey.trim(),
        op: customFieldOp,
        value: customFieldOp === 'exists' ? true : customFieldValue,
      };
    }

    return seg;
  }, [
    includeLabelIds,
    excludeLabelIds,
    includeListIds,
    excludeListIds,
    countryFilter,
    statusFilter,
    customFieldKey,
    customFieldOp,
    customFieldValue,
    activeTemplate?.category,
  ]);

  // Debounced estimation
  useEffect(() => {
    if (!open) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      setEstimating(true);
      try {
        const res = await whatsappEstimateReach({
          segment: currentSegment,
          templateId: selectedTemplateId || undefined,
          category: activeTemplate?.category,
          type: activeTemplate?.category === 'MARKETING' ? 'anytime' : 'anytime',
        });
        if (res.ok) {
          setEstimatedReach(res.count);
          setEstimatedCost(res.costEstimate);
        }
      } catch {
        // Fallback or quiet failure
      } finally {
        setEstimating(false);
      }
    }, 350);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [open, currentSegment, selectedTemplateId, activeTemplate?.category]);

  // Submit Handler (Draft vs Real Send)
  async function handleSubmit(asDraft: boolean) {
    if (!name.trim()) {
      toast.error(t('bcast.errors.nameRequired') || 'Campaign name is required');
      return;
    }

    if (channel === 'sms') {
      if (!selectedSmsProfileId) {
        toast.error(t('bcast.sms.profileRequired') || 'Please select an SMS Gateway profile');
        return;
      }
      if (!smsBody.trim()) {
        toast.error(t('bcast.sms.bodyRequired') || 'SMS message body cannot be empty');
        return;
      }
    } else {
      if (!selectedTemplateId || !activeTemplate) {
        toast.error(t('bcast.errors.templateRequired') || 'Please select an approved template');
        return;
      }

      if (accounts.numbers.length > 1 && !selectedAccountId) {
        toast.error(t('bcast.errors.accountRequired') || 'Please select a sending WhatsApp number');
        return;
      }

      if (hasMediaHeader && !headerMedia?.storagePath && !templateMediaPath && !asDraft) {
        toast.error(t('bcast.errors.mediaRequired') || 'Please upload the required header media');
        return;
      }
    }

    if (scheduleType === 'later' && !asDraft) {
      if (!scheduledDateTime) {
        toast.error(t('bcast.errors.scheduleTimeRequired') || 'Please select a scheduled date and time');
        return;
      }
      const scheduledEpoch = new Date(scheduledDateTime).getTime();
      if (isNaN(scheduledEpoch) || scheduledEpoch <= Date.now() + 60000) {
        toast.error(t('bcast.errors.futureTimeRequired') || 'Scheduled time must be at least 1 minute in the future');
        return;
      }
    }

    setSubmitting(true);
    try {
      const scheduledFor = scheduleType === 'later' && scheduledDateTime ? scheduledDateTime : undefined;

      let createRes: any;

      if (channel === 'sms') {
        createRes = await whatsappCreateBroadcast({
          name: name.trim(),
          channel: 'sms',
          smsProfileId: selectedSmsProfileId,
          smsBody: smsBody.trim(),
          type: 'anytime',
          segment: currentSegment,
          assignLabelAfter: assignLabelAfter || undefined,
          delaySeconds: delaySeconds > 0 ? delaySeconds : undefined,
          scheduledFor,
          timezone: scheduleType === 'later' ? timezone : undefined,
          dryRun: false,
        });
      } else {
        const mediaPayload: HeaderMedia | undefined = headerMedia?.storagePath
          ? {
              storagePath: headerMedia.storagePath,
              mime: headerMedia.mime,
              mediaType: mediaHeaderFormat.toLowerCase() as any,
            }
          : undefined;

        // 1. Create Broadcast Campaign
        createRes = await whatsappCreateBroadcast({
          name: name.trim(),
          channel: 'whatsapp',
          integrationId: selectedAccountId || undefined,
          templateId: activeTemplate!.id,
          templateName: activeTemplate!.name,
          templateLanguage: activeTemplate!.language,
          type: 'anytime',
          variableOverrides: Object.keys(variableOverrides).length > 0 ? variableOverrides : undefined,
          headerMedia: mediaPayload,
          segment: currentSegment,
          assignLabelAfter: assignLabelAfter || undefined,
          delaySeconds: delaySeconds > 0 ? delaySeconds : undefined,
          scheduledFor,
          timezone: scheduleType === 'later' ? timezone : undefined,
          dryRun: false,
        });
      }

      // 2. If not saving as draft, submit campaign immediately
      if (!asDraft && createRes.id) {
        await whatsappSubmitBroadcast({ broadcastId: createRes.id });
        toast.success(
          scheduleType === 'later'
            ? t('bcast.scheduledSuccess') || 'Campaign scheduled successfully!'
            : t('bcast.sendSuccess') || 'Broadcast campaign started!',
        );
      } else {
        toast.success(t('bcast.draftSaved') || 'Campaign saved as draft');
      }

      onSuccess();
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  // Label toggle helpers
  function toggleIncludeLabel(id: string) {
    setIncludeLabelIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    // Remove from exclude if included
    setExcludeLabelIds((prev) => prev.filter((x) => x !== id));
  }

  function toggleExcludeLabel(id: string) {
    setExcludeLabelIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    setIncludeLabelIds((prev) => prev.filter((x) => x !== id));
  }

  function toggleIncludeList(id: string) {
    setIncludeListIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    setExcludeListIds((prev) => prev.filter((x) => x !== id));
  }

  function toggleExcludeList(id: string) {
    setExcludeListIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    setIncludeListIds((prev) => prev.filter((x) => x !== id));
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('bcast.drawer.createTitle') || 'Create Broadcast Campaign'}
      subtitle={t('bcast.drawer.createSubtitle') || 'Configure audience targeting, message template, and delivery schedule.'}
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left column: Campaign Authoring Form */}
        <div className="space-y-6 lg:col-span-7">          {/* Section 1: Campaign Details */}
          <div className="rounded-xl border border-line bg-surface/50 p-4 space-y-4">
            <h3 className="text-sm font-bold text-ink flex items-center gap-2">
              <SparklesIcon size={16} className="text-brand" />
              {t('bcast.form.campaignDetails') || 'Campaign Details'}
            </h3>

            {/* Channel Switcher */}
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1.5">
                {t('bcast.form.channel') || 'Broadcast Channel'}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setChannel('whatsapp')}
                  className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-xs font-bold transition ${
                    channel === 'whatsapp'
                      ? 'border-brand bg-brand/10 text-brand'
                      : 'border-line bg-white text-ink-2 hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900'
                  }`}
                >
                  <SmartphoneIcon size={14} />
                  <span>WhatsApp</span>
                </button>
                <button
                  type="button"
                  onClick={() => setChannel('sms')}
                  className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-xs font-bold transition ${
                    channel === 'sms'
                      ? 'border-brand bg-brand/10 text-brand'
                      : 'border-line bg-white text-ink-2 hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900'
                  }`}
                >
                  <span>💬</span>
                  <span>SMS</span>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">
                {t('bcast.form.name') || 'Campaign Name'} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                placeholder={t('bcast.form.namePlaceholder') || 'e.g. Ramadan Special Offer, VIP Newsletter'}
                className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
            </div>

            {channel === 'whatsapp' && accounts.numbers.length > 1 && (
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">
                  {t('bcast.form.sendingNumber') || 'Send-From Number'} *
                </label>
                <select
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                >
                  {accounts.numbers.map((n) => (
                    <option key={n.id} value={n.id}>
                      {numberLabel(n)} {accounts.defaultId === n.id ? `(${t('common.default') || 'Default'})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Section 2: SMS vs WhatsApp Composer */}
          {channel === 'sms' ? (
            <div className="rounded-xl border border-line bg-surface/50 p-4 space-y-4">
              <h3 className="text-sm font-bold text-ink flex items-center gap-2">
                <span>💬</span>
                {t('bcast.sms.composerTitle') || 'SMS Message Content'}
              </h3>

              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">
                  {t('bcast.sms.selectProfile') || 'Select SMS Gateway Profile'} *
                </label>
                {loadingSmsProfiles ? (
                  <div className="py-2 text-xs text-ink-3">{t('common.loading') || 'Loading profiles…'}</div>
                ) : smsProfiles.length === 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
                    <p>{t('bcast.sms.noProfilesWarning') || 'No SMS Gateway profiles configured.'}</p>
                    <Link to="/integrations" className="font-bold underline mt-1 inline-block">
                      {t('bcast.sms.addProfileLink') || 'Add an SMS Gateway in Integrations Hub'} →
                    </Link>
                  </div>
                ) : (
                  <select
                    value={selectedSmsProfileId}
                    onChange={(e) => setSelectedSmsProfileId(e.target.value)}
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  >
                    {smsProfiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.provider.toUpperCase()}) {p.config.fromNumber || p.config.fromSenderId ? `· ${p.config.fromNumber || p.config.fromSenderId}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-ink-2">
                    {t('bcast.sms.bodyLabel') || 'SMS Message Body'} *
                  </label>
                  <button
                    type="button"
                    onClick={() => setSmsBody((prev) => prev + ' {{name}}')}
                    className="text-xs font-semibold text-brand hover:underline"
                  >
                    + {t('bcast.sms.insertName') || 'Insert {{name}}'}
                  </button>
                </div>
                <textarea
                  rows={4}
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                  placeholder={
                    t('bcast.sms.bodyPlaceholder') ||
                    'Hello {{name}}, we have a special discount for you! Reply STOP to opt out.'
                  }
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                />

                {/* Segment & Character Counter */}
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-ink-3 dark:text-slate-400 bg-white dark:bg-slate-900 p-2.5 rounded-lg border border-line dark:border-slate-800">
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                        smsCalc.encoding === 'Unicode'
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                          : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                      }`}
                    >
                      {smsCalc.encoding}
                    </span>
                    <span>
                      <strong>{smsCalc.charCount}</strong> {t('bcast.sms.chars') || 'chars'}
                    </span>
                    <span>·</span>
                    <span>
                      <strong>{smsCalc.segmentCount}</strong> {t('bcast.sms.segments') || 'segment(s)'}
                    </span>
                  </div>
                  <span>
                    {smsCalc.remainingInSegment} {t('bcast.sms.charsLeftInSegment') || 'left in segment'}
                  </span>
                </div>

                {smsCalc.encoding === 'Unicode' && (
                  <p className="mt-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                    {t('bcast.sms.unicodeNotice') ||
                      'Unicode characters detected (Arabic / Emoji). Message segment size is limited to 70 / 67 chars.'}
                  </p>
                )}
              </div>

              {/* Compliance Notice */}
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800 dark:border-sky-900/50 dark:bg-sky-950/40 dark:text-sky-300">
                <span className="font-semibold">{t('bcast.sms.complianceHeader') || 'SMS Compliance Notice'}:</span>{' '}
                {t(
                  'bcast.sms.complianceNotice') ||
                  'Opt-out compliance is enforced. STOP replies automatically unsubscribe recipients.'}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-line bg-surface/50 p-4 space-y-4">
              <h3 className="text-sm font-bold text-ink flex items-center gap-2">
                <SmartphoneIcon size={16} className="text-brand" />
                {t('bcast.form.messageTemplate') || 'Message Template'}
              </h3>

              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">
                  {t('bcast.form.selectTemplate') || 'Select Approved Template'} *
                </label>
                {loadingTemplates ? (
                  <div className="py-3 text-xs text-ink-3">Loading templates…</div>
                ) : approvedTemplates.length === 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    {t('bcast.form.noApprovedTemplates') ||
                      'No approved templates found for this account. Create and get templates approved in Bot Manager.'}
                  </div>
                ) : (
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                  >
                    {approvedTemplates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.name} ({tpl.language.toUpperCase()}) — {tpl.category}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Header Media Uploader if template requires media header */}
              {hasMediaHeader && (
                <div className="pt-2 border-t border-line">
                  <label className="block text-xs font-semibold text-ink-2 mb-2">
                    {t('bcast.form.headerMedia') || `Upload ${mediaHeaderFormat} Header`}
                    {templateMediaPath ? '' : ' *'}
                  </label>
                  <p className="mb-2 text-[11px] text-ink-3">
                    {templateMediaPath
                      ? t('bcast.form.headerMediaFromTemplate', { format: mediaHeaderFormat.toLowerCase() })
                      : t('bcast.form.headerMediaIsSent', { format: mediaHeaderFormat.toLowerCase() })}
                  </p>
                  <MediaUploader
                    mediaType={mediaHeaderFormat}
                    value={headerMedia}
                    onChange={(media, localPreview) => {
                      setHeaderMedia(media);
                      setLocalMediaPreview(localPreview || null);
                    }}
                    integrationId={selectedAccountId}
                  />
                </div>
              )}

              {/* Variable Mapping Overrides */}
              {templateVariables.length > 0 && (
                <div className="pt-2 border-t border-line space-y-2">
                  <label className="block text-xs font-semibold text-ink-2">
                    {t('bcast.form.variableOverrides') || 'Variable Values / Overrides'}
                  </label>
                  <p className="text-[11px] text-ink-3">
                    {t('bcast.form.variableOverridesHint') ||
                      'Provide static values for this campaign. By default, {{1}} uses subscriber name.'}
                  </p>
                  <div className="space-y-2">
                    {templateVariables.map((vNum) => (
                      <div key={vNum} className="flex items-center gap-2">
                        <span className="w-14 shrink-0 rounded bg-slate-100 px-2 py-1 text-center font-mono text-xs font-bold text-ink">
                          {`{{${vNum}}}`}
                        </span>
                        <input
                          type="text"
                          value={variableOverrides[vNum] || ''}
                          onChange={(e) =>
                            setVariableOverrides((prev) => ({
                              ...prev,
                              [vNum]: e.target.value,
                            }))
                          }
                          placeholder={
                            vNum === 1
                              ? t('bcast.form.var1Default') || 'Subscriber Name (default)'
                              : `Static value for {{${vNum}}}`
                          }
                          className="flex-1 rounded-lg border border-line bg-white px-3 py-1.5 text-xs text-ink focus:border-brand focus:outline-none"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section 3: Audience & Targeting */}
          <div className="rounded-xl border border-line bg-surface/50 p-4 space-y-4">
            <h3 className="text-sm font-bold text-ink flex items-center gap-2">
              <UserIcon size={16} className="text-brand" />
              {t('bcast.form.audienceTargeting') || 'Audience & Targeting'}
            </h3>

            {/* Labels include / exclude */}
            {labels.length > 0 && (
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-ink-2">
                  {t('bcast.form.labels') || 'Targeting by Labels'}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {labels.map((lbl) => {
                    const isInc = includeLabelIds.includes(lbl.id);
                    const isExc = excludeLabelIds.includes(lbl.id);
                    return (
                      <div
                        key={lbl.id}
                        className="inline-flex items-center rounded-lg border border-line bg-white p-1 text-xs"
                      >
                        <span
                          className="h-2.5 w-2.5 rounded-full me-1.5 bg-info"
                          style={lbl.color ? { backgroundColor: lbl.color } : undefined}
                        />
                        <span className="me-2 text-ink">{lbl.name}</span>
                        <button
                          type="button"
                          onClick={() => toggleIncludeLabel(lbl.id)}
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold transition-colors ${
                            isInc
                              ? 'bg-emerald-600 text-white'
                              : 'bg-slate-100 text-ink-2 hover:bg-slate-200'
                          }`}
                        >
                          + Inc
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleExcludeLabel(lbl.id)}
                          className={`ms-1 rounded px-1.5 py-0.5 text-[10px] font-bold transition-colors ${
                            isExc
                              ? 'bg-rose-600 text-white'
                              : 'bg-slate-100 text-ink-2 hover:bg-slate-200'
                          }`}
                        >
                          - Exc
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lists include / exclude */}
            {lists.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-line">
                <label className="block text-xs font-semibold text-ink-2">
                  {t('bcast.form.lists') || 'Targeting by Subscriber Lists'}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {lists.map((lst) => {
                    const isInc = includeListIds.includes(lst.id);
                    const isExc = excludeListIds.includes(lst.id);
                    return (
                      <div
                        key={lst.id}
                        className="inline-flex items-center rounded-lg border border-line bg-white p-1 text-xs"
                      >
                        <span className="me-2 text-ink font-medium">
                          {lst.name} ({lst.count || 0})
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleIncludeList(lst.id)}
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold transition-colors ${
                            isInc
                              ? 'bg-emerald-600 text-white'
                              : 'bg-slate-100 text-ink-2 hover:bg-slate-200'
                          }`}
                        >
                          + Inc
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleExcludeList(lst.id)}
                          className={`ms-1 rounded px-1.5 py-0.5 text-[10px] font-bold transition-colors ${
                            isExc
                              ? 'bg-rose-600 text-white'
                              : 'bg-slate-100 text-ink-2 hover:bg-slate-200'
                          }`}
                        >
                          - Exc
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Country & Opt-in Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-line">
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">
                  {t('bcast.form.countryFilter') || 'Country (ISO-2)'}
                </label>
                <input
                  type="text"
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  placeholder={t('bcast.countryFilterPlaceholder') || 'e.g. OM, SA, AE, US'}
                  className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-xs text-ink focus:border-brand focus:outline-none uppercase"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">
                  {t('bcast.form.optInStatus') || 'Opt-In Status'}
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-xs text-ink focus:border-brand focus:outline-none"
                >
                  <option value="subscribed">{t('bcast.form.statusSubscribedOnly') || 'Subscribed Only (Recommended)'}</option>
                  <option value="all">{t('bcast.form.statusAll') || 'All Statuses'}</option>
                </select>
              </div>
            </div>

            {/* Custom Field Condition */}
            {customFields.length > 0 && (
              <div className="pt-2 border-t border-line space-y-2">
                <label className="block text-xs font-semibold text-ink-2">
                  {t('bcast.form.customFieldFilter') || 'Custom Attribute Condition'}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <select
                    value={customFieldKey}
                    onChange={(e) => setCustomFieldKey(e.target.value)}
                    className="rounded-lg border border-line bg-white px-2 py-1.5 text-xs text-ink focus:border-brand focus:outline-none"
                  >
                    <option value="">{t('common.none') || '-- None --'}</option>
                    {customFields.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label} ({f.key})
                      </option>
                    ))}
                  </select>

                  <select
                    value={customFieldOp}
                    onChange={(e) => setCustomFieldOp(e.target.value as any)}
                    disabled={!customFieldKey}
                    className="rounded-lg border border-line bg-white px-2 py-1.5 text-xs text-ink focus:border-brand focus:outline-none disabled:opacity-50"
                  >
                    <option value="eq">equals</option>
                    <option value="neq">not equals</option>
                    <option value="contains">contains</option>
                    <option value="exists">exists / not empty</option>
                  </select>

                  <input
                    type="text"
                    value={customFieldValue}
                    onChange={(e) => setCustomFieldValue(e.target.value)}
                    disabled={!customFieldKey || customFieldOp === 'exists'}
                    placeholder="Value…"
                    className="rounded-lg border border-line bg-white px-3 py-1.5 text-xs text-ink focus:border-brand focus:outline-none disabled:opacity-50"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 4: Post-Send & Timing Options */}
          <div className="rounded-xl border border-line bg-surface/50 p-4 space-y-4">
            <h3 className="text-sm font-bold text-ink flex items-center gap-2">
              <ClockIcon size={16} className="text-brand" />
              {t('bcast.form.deliveryOptions') || 'Delivery & Automation'}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Assign Label After Send */}
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">
                  {t('bcast.form.assignLabelAfter') || 'Assign Label After Send'}
                </label>
                <select
                  value={assignLabelAfter}
                  onChange={(e) => setAssignLabelAfter(e.target.value)}
                  className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-xs text-ink focus:border-brand focus:outline-none"
                >
                  <option value="">{t('common.none') || '-- None --'}</option>
                  {labels.map((lbl) => (
                    <option key={lbl.name} value={lbl.name}>
                      {lbl.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Delay seconds between messages */}
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">
                  {t('bcast.form.delaySeconds') || 'Delay Between Messages'}
                </label>
                <select
                  value={delaySeconds}
                  onChange={(e) => setDelaySeconds(Number(e.target.value))}
                  className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-xs text-ink focus:border-brand focus:outline-none"
                >
                  <option value={0}>{t('bcast.form.delayImmediate') || 'Instant (Max Speed ~80 msgs/sec)'}</option>
                  <option value={1}>1 second delay (~1 msg/sec)</option>
                  <option value={2}>2 seconds delay</option>
                  <option value={5}>5 seconds delay</option>
                  <option value={10}>10 seconds delay</option>
                </select>
              </div>
            </div>

            {/* Schedule When */}
            <div className="pt-2 border-t border-line space-y-3">
              <label className="block text-xs font-semibold text-ink-2">
                {t('bcast.form.schedule') || 'Delivery Schedule'}
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-xs font-medium text-ink cursor-pointer">
                  <input
                    type="radio"
                    name="scheduleType"
                    checked={scheduleType === 'now'}
                    onChange={() => setScheduleType('now')}
                    className="text-brand focus:ring-brand"
                  />
                  {t('bcast.form.sendNow') || 'Send immediately after submission'}
                </label>
                <label className="flex items-center gap-2 text-xs font-medium text-ink cursor-pointer">
                  <input
                    type="radio"
                    name="scheduleType"
                    checked={scheduleType === 'later'}
                    onChange={() => setScheduleType('later')}
                    className="text-brand focus:ring-brand"
                  />
                  {t('bcast.form.sendLater') || 'Schedule for later date/time'}
                </label>
              </div>

              {scheduleType === 'later' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <div>
                    <label className="block text-xs font-semibold text-ink-2 mb-1">
                      {t('bcast.form.scheduledTime') || 'Date & Time'} *
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduledDateTime}
                      onChange={(e) => setScheduledDateTime(e.target.value)}
                      className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-xs text-ink focus:border-brand focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-ink-2 mb-1">
                      {t('bcast.form.timezone') || 'Timezone'}
                    </label>
                    <select
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-xs text-ink focus:border-brand focus:outline-none"
                    >
                      {COMMON_TIMEZONES.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column: Live Audience Estimate & Real-time Phone Preview */}
        <div className="space-y-6 lg:col-span-5">
          {/* Live Reach & Pricing Card */}
          <div className="rounded-xl border border-brand/20 bg-brand-50/50 p-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-brand mb-2 flex items-center justify-between">
              <span>{t('bcast.estimate.title') || 'Audience Reach & Cost'}</span>
              {estimating && <span className="animate-spin text-xs">⏳</span>}
            </h4>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="rounded-lg bg-white p-3 border border-brand/10">
                <span className="text-[11px] font-semibold text-ink-3 block">
                  {t('bcast.estimate.targetedSubscribers') || 'Targeted Audience'}
                </span>
                <span className="text-xl font-extrabold text-ink block mt-0.5">
                  {estimating ? '…' : estimatedReach !== null ? estimatedReach.toLocaleString() : '0'}
                </span>
              </div>

              <div className="rounded-lg bg-white p-3 border border-brand/10">
                <span className="text-[11px] font-semibold text-ink-3 block">
                  {t('bcast.estimate.estimatedCost') || 'Estimated Cost'}
                </span>
                <span className="text-xl font-extrabold text-emerald-600 block mt-0.5">
                  {estimating ? '…' : estimatedCost !== null ? `$${estimatedCost.toFixed(2)}` : '$0.00'}
                </span>
              </div>
            </div>

            <p className="text-[11px] text-ink-3 mt-3">
              {t('bcast.estimate.complianceNote') ||
                'Audience respects opt-out rules, marketing consent, and quiet hours. Rates depend on recipient destination country.'}
            </p>
          </div>

          {/* Phone Mockup Preview */}
          <div className="space-y-2">
            <label className="block text-xs font-bold uppercase tracking-wider text-ink-3">
              {t('bcast.preview.phoneMockup') || 'Message Preview'}
            </label>
            {channel === 'sms' ? (
              <div className="mx-auto w-full max-w-[280px] rounded-[32px] border-4 border-slate-800 bg-slate-900 p-3 shadow-xl">
                <div className="mx-auto mb-3 h-3.5 w-20 rounded-full bg-slate-800" />
                <div className="min-h-[320px] rounded-[22px] bg-slate-100 p-3 flex flex-col justify-end text-xs dark:bg-slate-800">
                  <div className="mb-auto text-center text-[10px] text-ink-3 dark:text-slate-400 font-semibold py-1">
                    {t('bcast.sms.smsMessage') || 'Text Message (SMS)'}
                  </div>
                  <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-brand text-white px-3.5 py-2.5 shadow-sm text-xs break-words">
                    {smsBody
                      ? smsBody.replace(/\{\{name\}\}/g, 'Ahmed Al-Harthi')
                      : t('bcast.sms.previewPlaceholder') || 'Your SMS message will appear here…'}
                  </div>
                  <div className="mt-1 text-[10px] text-ink-3 dark:text-slate-500">
                    {t('bcast.sms.now') || 'Now'}
                  </div>
                </div>
              </div>
            ) : activeTemplate ? (
              <TemplatePreview
                headerType={
                  headerComponent?.format === 'TEXT'
                    ? 'TEXT'
                    : hasMediaHeader
                    ? (headerComponent?.format as any)
                    : 'NONE'
                }
                headerText={(activeTemplate as any).headerText || ''}
                headerMediaUrl={localMediaPreview || (activeTemplate as any).headerMedia?.url}
                body={activeTemplate.body || ''}
                footer={(activeTemplate as any).footerText || ''}
                buttons={activeTemplate.components?.find((c) => c.type === 'BUTTONS')?.buttons as any}
                variables={Object.entries(variableOverrides).map(([k, v]) => ({
                  index: Number(k),
                  example: v,
                }))}
                businessName={numberLabel(selectedNumber) || 'WaTag Marketing'}
              />
            ) : (
              <div className="flex h-72 items-center justify-center rounded-2xl border-2 border-dashed border-line text-xs text-ink-3">
                {t('bcast.preview.selectTemplatePrompt') || 'Select an approved template to preview'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Drawer Action Buttons Footer */}
      <div className="mt-8 flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-line">
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          {t('common.cancel') || 'Cancel'}
        </Button>

        <Button
          variant="secondary"
          onClick={() => handleSubmit(true)}
          disabled={submitting || !(channel === 'sms' ? Boolean(name.trim() && selectedSmsProfileId && smsBody.trim()) : Boolean(name.trim() && selectedTemplateId))}
        >
          {submitting ? t('common.saving') || 'Saving…' : t('bcast.actions.saveDraft') || 'Save as Draft'}
        </Button>

        <Button
          variant="primary"
          onClick={() => handleSubmit(false)}
          disabled={submitting || !(channel === 'sms' ? Boolean(name.trim() && selectedSmsProfileId && smsBody.trim()) : Boolean(name.trim() && selectedTemplateId))}
        >
          {submitting
            ? t('common.processing') || 'Processing…'
            : scheduleType === 'later'
            ? t('bcast.actions.scheduleCampaign') || 'Schedule Campaign'
            : t('bcast.actions.sendNow') || 'Send Now'}
        </Button>
      </div>
    </Drawer>
  );
}
